import { IAlgorithmParams } from '../interfaces/algorithm-config.interface'
import { AlgorithmConfigStatus } from '../interfaces/algorithm-config.interface'

/** Severidad de `p-tag` por estado. Verde = la que está calculando ofertas ahora mismo. */
export const STATUS_SEVERITY: Record<AlgorithmConfigStatus, string> = {
  active: 'success',
  draft: 'warning',
  archived: 'secondary',
}

export const STATUS_LABEL: Record<AlgorithmConfigStatus, string> = {
  active: 'Activa',
  draft: 'Borrador',
  archived: 'Archivada',
}

/**
 * Los siete bloques, en el orden en que ocurren dentro del cálculo. Ese orden no es estético:
 * es el recorrido real —capacidad base → multiplicadores → tasa → plazo → oferta conservadora—,
 * así que leer el formulario de arriba a abajo es leer el algoritmo.
 */
export const PARAM_BLOCKS: Array<{ key: string; label: string; icon: string; hint: string }> = [
  { key: 'baseCapacity', label: 'Capacidad base', icon: 'fa-solid fa-coins', hint: 'Cuatro lecturas independientes de cuánto puede pagar el cliente. Se toma la más favorable.' },
  { key: 'riskMultipliers', label: 'Multiplicadores de riesgo', icon: 'fa-solid fa-shield-halved', hint: 'Seis factores que se multiplican entre sí sobre la capacidad base. Castigan rápido: seis factores de 0.85 dejan la capacidad en 38%.' },
  { key: 'rateTable', label: 'Tabla de tasas', icon: 'fa-solid fa-percent', hint: 'La tasa anual mejora por score y empeora por listas negras.' },
  { key: 'terms', label: 'Plazos', icon: 'fa-solid fa-calendar-days', hint: 'El plazo agresivo sale del margen; el conservador le resta meses.' },
  { key: 'conservativeOffer', label: 'Oferta conservadora', icon: 'fa-solid fa-shield', hint: 'Se deriva de la máxima, no se calcula aparte.' },
  { key: 'grades', label: 'Grades', icon: 'fa-solid fa-a', hint: 'Solo presentación: no entra al cálculo del monto ni de la tasa.' },
  { key: 'rounding', label: 'Redondeo', icon: 'fa-solid fa-calculator', hint: 'Decimales de las tasas en la respuesta.' },
]

/** Etiquetas legibles por campo. Sin esto el formulario mostraría los nombres del código. */
export const FIELD_LABELS: Record<string, string> = {
  'baseCapacity.netIncomeMonths': 'Meses de utilidad neta financiables',
  'baseCapacity.annualSalesFactor': 'Fracción de ventas anualizadas',
  'baseCapacity.avgBillingFactor': 'Fracción de facturación promedio',
  'baseCapacity.cashFlowFactor': 'Fracción del flujo de efectivo',
  'baseCapacity.cashFlowWindowMonths': 'Ventana del flujo de efectivo',
  'baseCapacity.salesSeriesWindowMonths': 'Ventana de la serie de ventas',
  'baseCapacity.fallbackNetIncomeRatio': 'Utilidad supuesta sin ingresos-vs-gastos',
  'baseCapacity.fallbackMarginNoRevenue': 'Margen supuesto sin ingresos',
  'baseCapacity.fallbackMarginNoIvG': 'Margen supuesto sin ingresos-vs-gastos',
  'riskMultipliers.score.defaultScore': 'Score supuesto cuando Syntage no da uno',
  'riskMultipliers.score.minScore': 'Score inicial de la normalización',
  'riskMultipliers.score.span': 'Rango de normalización',
  'riskMultipliers.score.slope': 'Pendiente',
  'riskMultipliers.score.intercept': 'Ordenada al origen',
  'riskMultipliers.score.floor': 'Piso del factor',
  'riskMultipliers.score.cap': 'Techo del factor',
  'riskMultipliers.blacklist.penaltyValue': 'Factor con listas negras',
  'riskMultipliers.blacklist.cleanValue': 'Factor sin listas negras',
  'rateTable.baseRate': 'Tasa base',
  'rateTable.blacklistSurchargePts': 'Recargo por listas negras',
  'terms.defaultMonths': 'Plazo por defecto',
  'terms.conservativeDeltaMonths': 'Meses que resta la oferta conservadora',
  'terms.conservativeFloorMonths': 'Piso del plazo conservador',
  'conservativeOffer.minAmountFactor': 'Fracción de la oferta máxima',
  'conservativeOffer.minAmountFactorWithBlacklist': 'Fracción con listas negras',
  'conservativeOffer.rateDiscountPts': 'Descuento de tasa',
  'conservativeOffer.minRateFloorPct': 'Piso de tasa',
  'conservativeOffer.revenueFloorFactor': 'Piso del monto como fracción del ingreso',
  'conservativeOffer.maxShareOfMax': 'Techo de la conservadora como fracción de la máxima',
  'rounding.rateDecimals': 'Decimales de las tasas',
}

/**
 * Campos que exigen una advertencia visible al editarlos. No es paranoia de diseño: cada uno tiene
 * una razón concreta detrás.
 */
export const DANGEROUS_FIELDS: Record<string, string> = {
  'riskMultipliers.score.defaultScore':
    'Es el parámetro más delicado de toda la configuración: aplica justo a los clientes de los que menos sabemos. Con 650 caen en el tramo de 29%, como si los hubiéramos evaluado.',
  'rateTable.baseRate': 'Es la tasa que reciben todos los clientes cuyo score no alcanza ningún tramo.',
  'conservativeOffer.minRateFloorPct': 'Ninguna oferta puede quedar por debajo de este piso, ni siquiera la conservadora.',
}

/**
 * Borrador local de la pantalla de edición.
 *
 * Existe porque hasta ahora salir de la pantalla —«Volver», el botón atrás, F5, o irse a leer el
 * manual— perdía **treinta y tantos campos editados sin avisar**. No hay `CanDeactivate` en el repo
 * y tampoco se quiere: el objetivo es poder consultar la documentación sin fricción y volver.
 */
export const DRAFT_KEY_PREFIX = 'renda-algorithm-config-draft'

/** Un borrador más viejo que esto se descarta sin preguntar: ya no representa lo que alguien quiso. */
export const DRAFT_TTL_DAYS = 7

/** Lo que se persiste. `paramsHash` delata que la versión del servidor cambió mientras tanto. */
export interface IAlgorithmConfigDraft {
  params: IAlgorithmParams
  nombre: string
  descripcion: string
  motivo: string
  /** Hash de los params del servidor cuando se empezó a editar. `null` en una versión nueva. */
  paramsHash: string | null
  savedAt: string
}
